import HighchartsPatternFill from 'highcharts-pattern-fill';
import Highcharts from 'highcharts/highstock';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { ForwardedRef } from 'react';
import {
  CHART_COLOR,
  ID_HR_SUMMARY_TAB,
  OTHER_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  eventListShowForAfibTab,
} from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import {
  selectActiveTab,
  selectControlModal,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectIsShowAfibEvent,
  selectIsShowArtifactEvent,
  selectJumpDate,
  selectSelectedColumnInfo,
  selectSelectedDateValue,
  selectSelectedEventIndex,
  useReportStore,
} from '@/entities/report/@x/heart-rate';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import {
  DAY_EPOCH,
  ZOOM_SCALE_DEFAULT,
  configChartHR,
  generateDataChartFromRawData,
  generateDataHRChart,
  generateZoneChart,
  getZoomScale,
  getZoomScaleIndexToSet,
  calculateZoomRangeOnSelect,
} from '../lib/chartHrHelper';
import type { ChartHRHandle, ChartHRProps } from '../ui/chartHr';

HighchartsPatternFill(Highcharts);

/**
 * Drives the daily heart-rate chart of a report tab, owning its Highcharts instance, zoom state and minute or event selection. Reads and writes the tab-keyed report store, builds the series from raw hrs, missing points and event indicators, wires the load, click and extremes handlers, exposes arrow navigation and selection reset through the forwarded ref, and re-selects columns when the jump date, visible event types or selected strip change.
 *
 * @param props - The chart inputs: tab key, chart data, selected strip, timezone offset, container id and the old-UI and daily-summary flags.
 * @param ref - Forwarded handle exposing arrow-button navigation and current-selection reset.
 * @returns An object carrying the two loading flags, isRenderingChart, isOldUI, idHtmlChart, zoomScale, zoomIndex, setExtremesRef and the reset, zoom-out and zoom-in handlers.
 */
const useChartHR = (
  {
    keyRecoil,
    updatedSelectedStrip = {},
    idHtmlChart,
    dataChart,
    timezoneOffset,
    isOldUI,
    isDailyHrSummary,
  }: Pick<
    ChartHRProps,
    | 'keyRecoil'
    | 'updatedSelectedStrip'
    | 'idHtmlChart'
    | 'dataChart'
    | 'timezoneOffset'
    | 'isOldUI'
    | 'isDailyHrSummary'
  >,
  ref: ForwardedRef<ChartHRHandle>,
) => {
  /**
   * Reads the event tab currently active for this chart's report tab. Pulls the value straight from the report store state outside React, so callers see it at call time rather than at the last render.
   *
   * @returns The active tab's event type.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Stores the sorted event and minute rows describing the selected chart column. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The selected column's info rows to store, or an updater producing them.
   * @returns The store action's result.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const setSelectedEventIndexAction = useReportStore((s) => s.actions.setSelectedEventIndex);
  /**
   * Stores which row of the selected column's info list is currently active. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The index of the active row, or an updater producing it.
   * @returns The store action's result.
   */
  const setSelectedEventIndex = (v: Parameters<typeof setSelectedEventIndexAction>[1]) =>
    setSelectedEventIndexAction(keyRecoil, v);
  const setNextColumnInfoAction = useReportStore((s) => s.actions.setNextColumnInfo);
  /**
   * Stores the event and minute rows describing the column after the selected one. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The next column's info rows to store, or an updater producing them.
   * @returns The store action's result.
   */
  const setNextColumnInfo = (v: Parameters<typeof setNextColumnInfoAction>[1]) =>
    setNextColumnInfoAction(keyRecoil, v);
  const setPrevColumnInfoAction = useReportStore((s) => s.actions.setPrevColumnInfo);
  /**
   * Stores the event and minute rows describing the column before the selected one. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The previous column's info rows to store, or an updater producing them.
   * @returns The store action's result.
   */
  const setPrevColumnInfo = (v: Parameters<typeof setPrevColumnInfoAction>[1]) =>
    setPrevColumnInfoAction(keyRecoil, v);
  const isShowArtifactEvent = useReportStore(selectIsShowArtifactEvent(keyRecoil));
  const isShowAfibEvent = useReportStore(selectIsShowAfibEvent(keyRecoil));
  // const isShowOtherEvent = useReportStore(selectIsShowOtherEvent(keyRecoil));
  const isShowOtherEvent = false;
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Sets whether this report tab's heart-rate chart is still loading. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The loading flag to store, or an updater producing it.
   * @returns The store action's result.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the heart-rate chart should jump to on this report tab. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The jump date to store, or an updater producing it.
   * @returns The store action's result.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time the heart-rate chart should jump to on this report tab. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The jump time epoch to store, or an updater producing it.
   * @returns The store action's result.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  const setChartHrsDataAction = useReportStore((s) => s.actions.setChartHrsData);
  /**
   * Stores the per-minute heart-rate summary rows backing this report tab's chart. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The heart-rate summary rows to store, or an updater producing them.
   * @returns The store action's result.
   */
  const setChartHrsData = (v: Parameters<typeof setChartHrsDataAction>[1]) =>
    setChartHrsDataAction(keyRecoil, v);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the ECG strip currently selected on this report tab. Forwards the value to the store action along with the tab key so the write lands on the right tab's record.
   *
   * @param v - The strip to select, or an updater producing it from the previous strip.
   * @returns The store action's result.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  /**
   * Reads the date currently selected on this report tab. Pulls the value straight from the report store state outside React, so callers see it at call time rather than at the last render.
   *
   * @returns The selected date value for the tab.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());

  const [zoomScale, setZoomScale] = useState<any>();
  const [zoomIndex, setZoomIndex] = useState(0);
  const [loadChartCount, setLoadChartCount] = useState(0);

  const dataDrawChart = useRef<any>(null);
  const chartRef = useRef<any>(null);
  const currentSelectedIndexRef = useRef<any>(null);
  const eventIndicatorsRef = useRef<any[]>([]);
  const isZoomBySelect = useRef(false);
  const maxHrRef = useRef<any>(null);
  const setExtremesRef = useRef<any[]>([]);
  const originalDataAreaChartZoneRef = useRef<any[]>([]);
  const originalDataColumnChartRef = useRef<any[]>([]);
  const preventReselectColumn = useRef(false);
  const preventSetSelectedStrip = useRef(false);
  const isSelectEventDeleted = useRef(false);
  const isRenderingChart = useRef(false);
  const hasClickedEvent = useRef(false);
  const currentSelectedStripId = useRef(updatedSelectedStrip?.id || null);
  const preventReSelectByJumpDate = useRef(false);

  /**
   * Reads the heart-rate spline point sitting at a given series index. Returns undefined when that point has no y value, so callers never act on an empty minute.
   *
   * @param index - The position in the heart-rate spline series to read.
   * @returns The heart-rate point at that index, or undefined when that minute has no value.
   */
  const findEleHrFromIndex = (index: any) => {
    const { data } = chartRef.current.series[2];
    const selectedHr = data[index];
    if (_.isNil(selectedHr?.y)) {
      return undefined;
    }
    return selectedHr;
  };

  /**
   * Looks up the heart-rate spline point whose x category matches a given minute epoch. Finds the category's position in the series xData and returns undefined when the point at that index carries no y value.
   *
   * @param category - The minute epoch to match against the series categories.
   * @returns The matching heart-rate point, or undefined when that minute has no value.
   */
  const findEleHrFromCategory = (category: any) => {
    const { data, xData } = chartRef.current.series[2];
    const index = xData?.indexOf(category);
    const selectedHr = data[index];
    if (_.isNil(selectedHr?.y)) {
      return undefined;
    }
    return selectedHr;
  };

  /**
   * Returns the heart-rate chart to its full unzoomed day view. Clears both x-axis extremes, drops the select-zoom flag and resets the zoom index and zoom scale state.
   *
   * @returns Nothing.
   */
  const handleResetZoom = () => {
    chartRef.current.xAxis[0].setExtremes(null, null);
    isZoomBySelect.current = false;
    setZoomIndex(0);
    setZoomScale(undefined);
  };

  const handleSetMinMaxNextExtremes = ({
    zoomScaleIndexToSet,
    min,
    max,
    dataMin,
    dataMax,
  }: any) => {
    const nextZoomRange = DAY_EPOCH / (ZOOM_SCALE_DEFAULT[zoomScaleIndexToSet] / 100);
    if (!isZoomBySelect.current && currentSelectedIndexRef.current) {
      const selectedHr = findEleHrFromIndex(currentSelectedIndexRef.current);
      const tempMin = selectedHr.category - nextZoomRange / 2;
      const tempMax = selectedHr.category + nextZoomRange / 2;
      const outOfRangeMax = tempMax - dataMax;
      const outOfRangeMin = tempMin - dataMin;
      const isOutOfRangeMax = outOfRangeMax > 0;
      const isOutOfRangeMin = outOfRangeMin < 0;
      let calculateMin;
      let calculateMax;
      if (isOutOfRangeMax) {
        calculateMin = tempMin - Math.abs(outOfRangeMax);
        calculateMax = dataMax;
      } else if (isOutOfRangeMin) {
        calculateMin = dataMin;
        calculateMax = tempMax + Math.abs(outOfRangeMin);
      } else {
        calculateMin = tempMin;
        calculateMax = tempMax;
      }
      chartRef.current.xAxis[0].setExtremes(calculateMin, calculateMax);
    } else {
      chartRef.current.xAxis[0].setExtremes(min, min + nextZoomRange);
    }
  };

  const debounceRedraw = _.debounce((chart) => {
    if (chart && chart.legend) {
      chart.redraw();
    }
  }, 10);

  /**
   * Steps the heart-rate chart one level back out of the preset zoom scale. Resolves the next lower zoom index, delegates to the full reset when that index is zero, otherwise applies the widened extremes and stores the index while clearing any custom scale.
   *
   * @returns Nothing.
   */
  const handleZoomOut = () => {
    const { min, max, dataMax, dataMin } = chartRef.current.xAxis[0];
    const zoomScalee = getZoomScale(min, max);
    const zoomScaleIndexToSet = getZoomScaleIndexToSet(zoomScalee);
    if (zoomScaleIndexToSet === 0) {
      handleResetZoom();
      return;
    }
    handleSetMinMaxNextExtremes({
      zoomScaleIndexToSet,
      min,
      max,
      dataMin,
      dataMax,
    });
    setZoomIndex(zoomScaleIndexToSet);
    setZoomScale(undefined);
  };

  /**
   * Steps the heart-rate chart one level deeper into the preset zoom scale. Reads the current extremes, resolves the next zoom index above the current percentage, applies the narrowed min and max around the selected point, then stores the index and clears any custom scale.
   *
   * @returns Nothing.
   */
  const handleZoomIn = () => {
    const { min, max, dataMin, dataMax } = chartRef.current.xAxis[0];
    const zoomScalee = getZoomScale(min, max);
    const zoomScaleIndexToSet = getZoomScaleIndexToSet(zoomScalee, true);
    handleSetMinMaxNextExtremes({
      zoomScaleIndexToSet,
      min,
      max,
      dataMin,
      dataMax,
    });
    setZoomIndex(zoomScaleIndexToSet);
    setZoomScale(undefined);
  };

  /**
   * Restores the previously selected minute's column to its original appearance. Reads the saved className, y and color from the column snapshot and updates the column series without redrawing, additionally deselecting the matching spline point on the new UI.
   *
   * @returns Nothing.
   */
  const resetPreviousPoint = () => {
    if (!_.isNil(currentSelectedIndexRef.current)) {
      if (isOldUI) {
        const { y, color, className } =
          originalDataColumnChartRef.current[currentSelectedIndexRef.current];
        chartRef.current.series[1].data[currentSelectedIndexRef.current].update(
          {
            className,
            y,
            color,
          },
          false,
        );
      } else {
        const { y, color, className } =
          originalDataColumnChartRef.current[currentSelectedIndexRef.current];
        chartRef.current.series[1].data[currentSelectedIndexRef.current].update(
          {
            className,
            y,
            color,
          },
          false,
        );
        chartRef.current.series[2].data[currentSelectedIndexRef.current].update(
          { selected: false },
          false,
        );
      }
    }
  };

  /**
   * Keeps a newly selected minute visible when the heart-rate chart is zoomed in. Computes the zoom percentage from the current extremes and, above 100 percent, recentres the window on the point's category unless it is already in view, clamping the computed min and max to the data range.
   *
   * @param selectedHr - The heart-rate point being selected, whose category drives the recentring.
   * @returns Nothing.
   */
  const handleSetRangeWhenZooming = (selectedHr: any) => {
    const { min, max, dataMax, dataMin } = chartRef.current.xAxis[0];
    const { category } = selectedHr;
    const range = calculateZoomRangeOnSelect({ min, max, dataMin, dataMax, category });
    if (range) {
      chartRef.current.xAxis[0].setExtremes(range.calculateMin, range.calculateMax);
    }
  };

  // *: Reset to original zones in cases:
  // - Click to single event
  // - Click to non-event column
  /**
   * Puts the heart-rate area chart back on its unhighlighted colour zones, used after clicking a single event or a non-event column. Clones the saved original zone array onto the area series without triggering a redraw.
   *
   * @returns Nothing.
   */
  const resetToOriginalZones = () => {
    const newZones = _.cloneDeep(originalDataAreaChartZoneRef.current);
    chartRef.current.series[0].update({ zones: newZones }, false);
  };

  /**
   * Recolours the area chart's zones to mark the span of the selected ECG event. Acts only on events at least a minute long, clamps the start and stop epochs into the visible day, picks light and dark artifact, AFib or other colours plus SVG patterns for unsaved events, then either recolours every overlapped zone or pushes new zone boundaries before reordering and applying them.
   *
   * @param foundEvent - The selected event whose start, stop and unsaved flags define the span and colour.
   * @param isArtifactEvent - Whether the event is an artifact, selecting the steel-gray palette.
   * @param isAfibEvent - Whether the event is AFib, selecting the amber palette.
   * @returns Nothing.
   */
  const highlightSelectedEventArea = ({ foundEvent, isArtifactEvent, isAfibEvent }: any) => {
    const foundEventStop = moment(foundEvent.stop);
    const foundEventStart = moment(foundEvent.start);
    if (moment(foundEvent.stop).diff(foundEvent.start, 'minutes') >= 1) {
      let foundEventStartEpoch = foundEventStart.clone().startOf('minutes').valueOf();
      let foundEventStopEpoch = foundEventStop.clone().startOf('minutes').valueOf();
      if (foundEventStopEpoch > dataDrawChart.current.firstEpoch + DAY_EPOCH) {
        foundEventStopEpoch = dataDrawChart.current.firstEpoch + DAY_EPOCH;
      }
      if (foundEventStartEpoch <= dataDrawChart.current.firstEpoch) {
        foundEventStartEpoch = dataDrawChart.current.firstEpoch;
      }
      let newZones = _.cloneDeep(originalDataAreaChartZoneRef.current);
      let selectedLeftZone: any;
      let selectedRightZone: any;
      let selectedLeftZoneIndex: any;
      let selectedRightZoneIndex: any;
      _.forEach(newZones, (x, index) => {
        if (!_.isNil(x?.value) && foundEventStartEpoch >= x.value) {
          selectedLeftZone = x;
          selectedLeftZoneIndex = index;
        }
        if (!selectedRightZone && !_.isNil(x?.value) && foundEventStopEpoch <= x.value) {
          selectedRightZone = x;
          selectedRightZoneIndex = index;
        }
      });
      const idSelectedUnsaveArtifactPatternURL = `url(#selected-unsave-artifact-event-custom-pattern-${idHtmlChart})`;
      const idSelectedUnsaveAfibPatternURL = `url(#selected-unsave-afib-event-custom-pattern-${idHtmlChart})`;
      const idSelectedUnsaveOtherPatternURL = `url(#selected-unsave-other-event-custom-pattern-${idHtmlChart})`;
      const idUnsaveAfibPatternUrl = `url(#unsave-afib-event-custom-pattern-${idHtmlChart})`;
      const idMissingDataWithUnsaveAfibPatternUrl = `url(#missing-data-with-unsave-afib-custom-pattern-${idHtmlChart})`;
      const idSelectedMissingDataWithUnsaveAfibPatternUrl = `url(#selected-missing-data-with-unsave-afib-custom-pattern-${idHtmlChart})`;
      const color = isArtifactEvent
        ? CHART_COLOR.STEEL_GRAY_LIGHT
        : isAfibEvent
          ? CHART_COLOR.AMBER_YELLOW_LIGHT
          : CHART_COLOR.CRIMSON_RED_LIGHT;
      const selectedColor = isArtifactEvent
        ? foundEvent.isNew || foundEvent.isChanged
          ? idSelectedUnsaveArtifactPatternURL
          : CHART_COLOR.STEEL_GRAY_DARK
        : isAfibEvent
          ? foundEvent.isNew || foundEvent.isChanged
            ? idSelectedUnsaveAfibPatternURL
            : CHART_COLOR.AMBER_YELLOW_DARK
          : foundEvent.isNew || foundEvent.isChanged
            ? idSelectedUnsaveOtherPatternURL
            : CHART_COLOR.CRIMSON_RED_DARK;
      if (
        selectedLeftZoneIndex > -1 &&
        selectedLeftZone?.value === foundEventStartEpoch &&
        selectedRightZoneIndex > -1 &&
        selectedRightZone?.value === foundEventStopEpoch
      ) {
        // *: Case: click to event which is same to start and stop of exist event zone
        // (event is a event zone)
        // * Chèn màu vào khi event overlap nhiều zones
        const range = selectedRightZoneIndex - selectedLeftZoneIndex;

        if (range > 1) {
          for (let i = selectedLeftZoneIndex; i <= selectedRightZoneIndex; i += 1) {
            // *: Case AFib event cross to no data zone
            const zone = newZones[i];
            if (
              (isArtifactEvent && _.includes(zone?.color, CHART_COLOR.STEEL_GRAY)) ||
              (!isArtifactEvent && !isAfibEvent && _.includes(zone?.color, CHART_COLOR.CRIMSON_RED))
            ) {
              _.assign(zone, {
                value: newZones[i].value,
                color: selectedColor,
                time: convertToMomentJs(newZones[i].value, timezoneOffset).format('HH:mm'),
              });
            } else if (isAfibEvent) {
              if (
                _.includes([CHART_COLOR.AMBER_YELLOW, CHART_COLOR.AMBER_YELLOW_LIGHT], zone?.color)
              ) {
                _.assign(zone, {
                  value: newZones[i].value,
                  color: selectedColor,
                  time: convertToMomentJs(newZones[i].value, timezoneOffset).format('HH:mm'),
                });
              } else if (_.includes([idUnsaveAfibPatternUrl], zone?.color)) {
                _.assign(zone, {
                  value: newZones[i].value,
                  color: idSelectedUnsaveAfibPatternURL,
                  time: convertToMomentJs(newZones[i].value, timezoneOffset).format('HH:mm'),
                });
              } else if (_.includes([idMissingDataWithUnsaveAfibPatternUrl], zone?.color)) {
                _.assign(zone, {
                  value: newZones[i].value,
                  color: idSelectedMissingDataWithUnsaveAfibPatternUrl,
                  time: convertToMomentJs(newZones[i].value, timezoneOffset).format('HH:mm'),
                });
              }
            }
          }
        } else {
          _.assign(newZones[selectedRightZoneIndex], {
            value: selectedRightZone.value,
            color: selectedColor,
            time: convertToMomentJs(selectedRightZone.value, timezoneOffset).format('HH:mm'),
          });
        }
      } else if (selectedLeftZoneIndex > -1 && selectedLeftZone?.value === foundEventStartEpoch) {
        // *: Case: click to event which is same to start and different from stop of exist event zone
        // (event is a part of event zone)
        newZones.push({
          value: foundEventStopEpoch,
          color: selectedColor,
          time: convertToMomentJs(foundEventStopEpoch, timezoneOffset).format('HH:mm'),
        });
      } else if (selectedRightZoneIndex > -1 && selectedRightZone?.value === foundEventStopEpoch) {
        // *: Case: click to event which is different from start and same to stop of exist event zone
        // (event is a part of event zone)
        newZones.push({
          value: foundEventStartEpoch,
          color,
          time: convertToMomentJs(foundEventStartEpoch, timezoneOffset).format('HH:mm'),
        });
        _.assign(newZones[selectedRightZoneIndex], {
          value: selectedRightZone.value,
          color: selectedColor,
          time: convertToMomentJs(selectedRightZone.value, timezoneOffset).format('HH:mm'),
        });
      } else {
        // *: Case: click to event which is different from start and stop of exist event zone
        // (event is a part of event zone)
        newZones.push(
          {
            value: foundEventStartEpoch,
            color,
            time: convertToMomentJs(foundEventStartEpoch, timezoneOffset).format('HH:mm'),
          },
          {
            value: foundEventStopEpoch,
            color: selectedColor,
            time: convertToMomentJs(foundEventStopEpoch, timezoneOffset).format('HH:mm'),
          },
        );
      }
      newZones = _.orderBy(newZones, ['value'], ['asc']);
      chartRef.current.series[0].update({ zones: newZones }, false);
    } else {
      // resetToOriginalZones();
    }
  };

  const handleSelectColumn = ({
    series = chartRef.current.series,
    selectedHr,
    eventStart,
    isEventDeleted = false,
    chart = chartRef.current,
    isUpdateByJump = false,
  }: any) => {
    if (!selectedHr) {
      return;
    }

    isSelectEventDeleted.current = isEventDeleted;
    handleSetRangeWhenZooming(selectedHr);
    const selectedColumn = series[1].data[selectedHr.index];
    const splineSelected = series[2].data[selectedHr.index];
    const prevColumn = series[2].data[selectedHr.index - 1];
    const nextColumn = series[2].data[selectedHr.index + 1];
    const activeButton = getActiveButton();
    const eventsInSelectedColumn: any[] = [];
    const eventsInPrevColumn: any[] = [];
    const eventsInNextColumn: any[] = [];
    // const isMainEvent = x => (eventStart ? x.start === eventStart && x.type === activeButton : x.type === activeButton);
    /**
     * Decides whether an event found in the clicked minute is the one the selection should centre on. Matches on start time when the column handler was given an eventStart, otherwise on the event type matching the active tab.
     *
     * @param x - The candidate event indicator overlapping the clicked column.
     * @returns True when the event is the main one for the current selection.
     */
    const isMainEvent = (x: any) => (eventStart ? x.start === eventStart : x.type === activeButton);
    //* Get all match with current click position
    _.forEach(eventIndicatorsRef.current, (x) => {
      if (
        x.type === TYPE_BEAT_ENUM.DELETED ||
        (x.type === TYPE_RHYTHM_EVENT_ENUM.AFIB && !dataDrawChart.current.isShowAfibEvent) ||
        (x.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
          !dataDrawChart.current.isShowArtifactEvent) ||
        (![
          TYPE_BEAT_ENUM.DELETED,
          TYPE_RHYTHM_EVENT_ENUM.AFIB,
          TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
        ].includes(x.type) &&
          !dataDrawChart.current.isShowOtherEvent)
      )
        return;
      if (x.startIndex <= selectedColumn.index && x.stopIndex >= selectedColumn.index) {
        eventsInSelectedColumn.push(x);
      }
      if (x.startIndex <= prevColumn?.index && x.stopIndex >= prevColumn?.index) {
        eventsInPrevColumn.push(x);
      }
      if (x.startIndex <= nextColumn?.index && x.stopIndex >= nextColumn?.index) {
        eventsInNextColumn.push(x);
      }
    });
    const prevColumnInfo = prevColumn?.y
      ? [
          ..._.map(eventsInPrevColumn, (event) => ({
            ...event,
            index: prevColumn.index,
            startTimeEpoch: prevColumn.category,
          })).filter(Boolean),
          {
            index: prevColumn.index,
            id: prevColumn.category,
            startTimeEpoch: prevColumn.category,
          },
        ]
      : [];
    const nextColumnInfo = nextColumn?.y
      ? [
          ..._.map(eventsInNextColumn, (event) => ({
            ...event,
            index: nextColumn.index,
            startTimeEpoch: nextColumn.category,
          })).filter(Boolean),
          {
            index: nextColumn.index,
            id: nextColumn.category,
            startTimeEpoch: nextColumn.category,
          },
        ]
      : [];
    setNextColumnInfo(nextColumnInfo);
    setPrevColumnInfo(prevColumnInfo);
    let foundEvent;
    // activeTabState's TBeatHrActiveTab member is a numeric enum value, not the 'hr-summary' string ID_HR_SUMMARY_TAB holds - atom-type gap, left as-is.
    if ((activeButton as any) === ID_HR_SUMMARY_TAB) {
      foundEvent =
        _.find(eventsInSelectedColumn, (x) => isMainEvent(x)) || eventsInSelectedColumn[0];
    } else {
      const eventsActiveTypeInSelectedColumn = _.filter(
        eventsInSelectedColumn,
        (x) => x.type === activeButton,
      );
      foundEvent =
        _.find(eventsActiveTypeInSelectedColumn, (x) => isMainEvent(x)) ||
        eventsActiveTypeInSelectedColumn[0] ||
        eventsInSelectedColumn[0];
    }
    // if click position have priority event then set it as main event
    // const foundEvent = _.find(eventsInSelectedColumn, x => isMainEvent(x)) || eventsInSelectedColumn[0];
    if (foundEvent) {
      const eventType = foundEvent.type || foundEvent.oldType;
      const isArtifactEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      if (isOldUI) {
        selectedColumn.update(
          {
            className: isArtifactEvent
              ? '--artifact-event-clicked'
              : isAfibEvent
                ? '--afib-event-clicked'
                : '--event-clicked',
            color: isArtifactEvent
              ? CHART_COLOR.STEEL_GRAY_LIGHT
              : isAfibEvent
                ? CHART_COLOR.AMBER_YELLOW_LIGHT
                : CHART_COLOR.CRIMSON_RED_LIGHT,
            y: maxHrRef.current,
          },
          false,
        );
      } else {
        selectedColumn.update(
          {
            className: isArtifactEvent
              ? '--artifact-event-clicked'
              : isAfibEvent
                ? '--afib-event-clicked'
                : '--event-clicked',
            color: isArtifactEvent
              ? CHART_COLOR.STEEL_GRAY_LIGHT
              : isAfibEvent
                ? CHART_COLOR.AMBER_YELLOW_LIGHT
                : CHART_COLOR.CRIMSON_RED_LIGHT,
            y: maxHrRef.current,
          },
          false,
        );
        splineSelected.update({ selected: true }, false);
      }
      highlightSelectedEventArea({ foundEvent, isArtifactEvent, isAfibEvent });
      const mainEvent = {
        ...foundEvent,
        index: selectedColumn.index,
        startTimeEpoch: selectedColumn.category,
      };
      const minuteInfo = {
        index: selectedColumn.index,
        id: selectedColumn.category,
        startTimeEpoch: selectedColumn.category,
        maxHr: selectedHr.maxHr,
        minHr: selectedHr.minHr,
        avgHr: selectedHr.avgHr,
        eventStart,
      };
      const columnsInfo = [
        mainEvent,
        ..._.map(eventsInSelectedColumn, (event) => {
          if (event?.idEvent !== mainEvent?.idEvent || event.id !== mainEvent.id) {
            return {
              ...event,
              index: selectedColumn.index,
              startTimeEpoch: selectedColumn.category,
            };
          }
          return null;
        }).filter(Boolean),
        minuteInfo,
      ];
      //* Event order: time -> Afib -> other -> artifact -> minute info
      const sortedColumnInfo = _.sortBy(columnsInfo, [
        (event) => event?.type !== activeButton,
        (event) => event?.start,
        (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
        (event) => !OTHER_EVENT_LIST.includes(event.type),
        (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
      ]);
      if (isDailyHrSummary) {
        //* Default select minute info in daily hr summary
        setSelectedEventIndex(sortedColumnInfo.length - 1);
      } else {
        //* Set index before selected column info because useUpdateEffect(()=>{},selectedEventIndex, selectedColumnInfo) not update selectedEventIndex value => need check
        const foundMainEventIndex = _.findIndex(sortedColumnInfo, (x) => isMainEvent(x));
        setSelectedEventIndex(foundMainEventIndex);
        if (isUpdateByJump && foundMainEventIndex < 0) {
          preventReSelectByJumpDate.current = true;
        }
      }
      setSelectedColumnInfo(sortedColumnInfo);
    } else {
      resetToOriginalZones();
      if (isOldUI) {
        selectedColumn.update({ className: '--chart-clicked', y: maxHrRef.current }, false);
      } else {
        splineSelected.update({ selected: true }, false);
      }
      const minuteInfo = {
        index: selectedColumn.index,
        id: selectedColumn.category,
        startTimeEpoch: selectedColumn.category,
        maxHr: selectedHr.maxHr,
        minHr: selectedHr.minHr,
        avgHr: selectedHr.avgHr,
        eventStart,
      };
      setSelectedEventIndex(0);
      setSelectedColumnInfo([minuteInfo]);
    }
    if (currentSelectedIndexRef.current !== selectedColumn.index) {
      resetPreviousPoint();
    }
    currentSelectedIndexRef.current = selectedColumn.index;

    // *: Redraw chart after update
    if (chart && chart.legend) {
      chart.redraw();
    }

    if (jumpDate) {
      setJumpDate(undefined);
      setJumpTime(moment(jumpDate).valueOf());
    }
  };

  /**
   * Sets the initial x-axis window once the heart-rate chart finishes loading. Restores the stored extremes when the chart was redrawn during a zoom, otherwise clears them and applies the default full-day range, then bumps the load counter and clears the chart loading flag.
   *
   * @param e - The Highcharts load event whose target exposes the x-axis to set extremes on.
   * @returns Nothing.
   */
  const eventLoad = (e: any) => {
    if (
      dataDrawChart.current?.isZoomAfterDrawChart &&
      // && !_.isEmpty(updatedSelectedStrip)
      !_.isEmpty(setExtremesRef.current)
    ) {
      e.target.xAxis[0].setExtremes(setExtremesRef.current[0], setExtremesRef.current[1]);
    } else {
      setExtremesRef.current = [];
      const nextZoomRange = DAY_EPOCH / (ZOOM_SCALE_DEFAULT[0] / 100); // default 100%
      const { min } = e.target.xAxis[0];
      e.target.xAxis[0].setExtremes(min, min + nextZoomRange);
    }
    setLoadChartCount((prev) => prev + 1);
    setIsLoadingHrChart(false);
  };

  /**
   * Handles a click anywhere on the heart-rate plot area by selecting the nearest minute that has data. Uses the spline series nearest-neighbour searchPoint lookup, and when the found point differs from the current selection marks the click as user-driven, runs the column selection routine and records the selected strip id.
   *
   * @param e - The Highcharts click event used to locate the nearest point.
   * @returns Nothing.
   */
  const eventClick = (e: any) => {
    //* Built in function that auto find nearest point have data using KNNeighbor algorithm
    const selectedHr = chartRef.current.series[2].searchPoint(e, true);
    if (selectedHr?.index !== currentSelectedIndexRef.current) {
      hasClickedEvent.current = true;
      handleSelectColumn({
        selectedHr,
      });
      currentSelectedStripId.current = updatedSelectedStrip?.id;
    }
  };

  /**
   * Tracks the heart-rate chart's zoom level whenever its x-axis extremes change. Flags select-driven zooms, computes the zoom percentage from min and max, keeps the extremes in a ref only while zoomed past 100 percent, and either snaps state to a preset zoom index or stores the raw scale.
   *
   * @param e - The Highcharts afterSetExtremes event carrying min, max and trigger.
   * @returns Nothing.
   */
  const eventAfterSetExtremes = (e: any) => {
    const { min, max, trigger } = e;
    if (trigger === 'zoom') {
      isZoomBySelect.current = true;
    }
    const zoomScalee = getZoomScale(min, max);
    //* isZooming
    if (zoomScalee > 100) {
      setExtremesRef.current = [min, max];
    } else {
      setExtremesRef.current = [];
    }
    const foundZoomScaleIndex = ZOOM_SCALE_DEFAULT.indexOf(zoomScalee);
    if (foundZoomScaleIndex !== -1) {
      setZoomScale(undefined);
      setZoomIndex(foundZoomScaleIndex);
    } else {
      setZoomScale(zoomScalee);
    }
  };

  /**
   * Handles a click on an individual heart-rate spline point. When the clicked index differs from the current selection it marks the click as user-driven, resolves the point through the index lookup that skips valueless minutes, runs the column selection and records the selected strip id.
   *
   * @param e - The Highcharts point click event whose point index identifies the minute.
   * @returns Nothing.
   */
  const eventClickSplineChart = (e: any) => {
    if (e.point.index !== currentSelectedIndexRef.current) {
      hasClickedEvent.current = true;
      handleSelectColumn({
        selectedHr: findEleHrFromIndex(e.point.index),
      });
      currentSelectedStripId.current = updatedSelectedStrip?.id;
    }
  };

  /**
   * Refreshes the heart-rate chart's series in place after the visible event types change. Bails out when no chart exists, regenerates the area, zone and column data, re-snapshots the originals, updates both series and redraws, then reselects the current minute through the index lookup.
   *
   * @returns Nothing.
   */
  const updateChartHr = () => {
    if (_.isEmpty(chartRef.current)) {
      return;
    }
    const { dataAreaChart, dataAreaChartZone, dataColumnChart } = generateDataHRChart(
      dataDrawChart.current,
    );
    originalDataAreaChartZoneRef.current = _.cloneDeep(dataAreaChartZone);
    originalDataColumnChartRef.current = _.cloneDeep(dataColumnChart);
    chartRef.current.series[0].update({ zones: dataAreaChartZone, data: dataAreaChart }, false);
    chartRef.current.series[1].update({ data: dataColumnChart }, false);
    if (chartRef.current) {
      chartRef.current.redraw();
    }
    handleSelectColumn({
      selectedHr: findEleHrFromIndex(currentSelectedIndexRef.current),
    });
  };

  /**
   * Renders the daily heart-rate Highcharts stock chart into the tab's chart container. Builds the area, zone and column series with generateDataHRChart from the cached draw data, snapshots the zone and column arrays into refs so later selections can be undone, assembles the config with the load, click and setExtremes handlers, and times the render with a LOADTIMEHRCHART console timer.
   *
   * @returns Nothing.
   */
  const drawChartHr = () => {
    console.time('LOADTIMEHRCHART');
    // generate data for chart
    const { dataAreaChart, dataAreaChartZone, dataColumnChart, dataHrsData, dataNegativeHrsZones } =
      generateDataHRChart(dataDrawChart.current);
    originalDataAreaChartZoneRef.current = _.cloneDeep(dataAreaChartZone);
    originalDataColumnChartRef.current = _.cloneDeep(dataColumnChart);
    const configChart = configChartHR({
      dataAreaChart,
      dataAreaChartZone,
      dataColumnChart,
      dataHrsData,
      dataNegativeHrsZones,
      minHr: dataDrawChart.current.minHr,
      maxHr: dataDrawChart.current.maxHr,
      timezoneOffset,
      eventLoad,
      eventClick,
      eventAfterSetExtremes,
      idHtmlChart,
      firstEpoch: dataDrawChart.current.firstEpoch,
      isOldUI,
    });
    chartRef.current = Highcharts.stockChart(idHtmlChart, configChart, (chart) => {
      console.timeEnd('LOADTIMEHRCHART');
      // Without the accessibility module, Highcharts falls back to
      // role="img" + aria-label from the (hidden) title text — empty here,
      // so the chart has no accessible name. Chart.onload sets that fallback
      // itself right after this callback runs, so override it a tick later.
      setTimeout(() => {
        chart.renderer.boxWrapper.attr({ 'aria-label': 'Heart rate chart' });
      }, 0);
    });
  };

  const createHrChart = ({
    hrs,
    missingPoints,
    artifactEventGuides,
    artifactEventAreaPoints,
    afibEventGuides,
    afibEventAreaPoints,
    otherEventGuides,
    otherEventAreaPoints,
    unsaveArtifactEvents,
    unsaveAfibEvents,
    unsaveOtherEvents,
    minHr,
    maxHr,
    firstEpoch,
    isZoomAfterDrawChart,
    eventIndicators,
  }: any) => {
    const {
      hrsData,
      missingPointsData,
      artifactEventGuidesData,
      artifactEventAreaPointsData,
      afibEventGuidesData,
      afibEventAreaPointsData,
      otherEventGuidesData,
      otherEventAreaPointsData,
      missingPointsZone,
      unsaveArtifactEventsZones,
      unsaveAfibEventsZones,
      unsaveOtherEventsZones,
      negativeHrsZones,
    } = generateDataChartFromRawData({
      hrs,
      missingPoints,
      artifactEventGuides,
      artifactEventAreaPoints,
      afibEventGuides,
      afibEventAreaPoints,
      otherEventGuides,
      otherEventAreaPoints,
      unsaveArtifactEvents,
      unsaveAfibEvents,
      unsaveOtherEvents,
      timezoneOffset,
      minHr,
      eventClickSplineChart,
      idHtmlChart,
      eventIndicators,
    });

    // chartHrsDataState (Recoil, Task 7) is typed IHrChartData[], which requires events/time/tooltipText/x/y
    // this mapped object doesn't set - pre-existing gap.
    setChartHrsData(
      _.map(hrsData, (x) => ({
        id: x.id,
        index: x.index,
        avgHr: x.avgHr,
        maxHr: x.maxHr,
        minHr: x.minHr,
      })) as any,
    );

    // generate zone
    const { zoneArtifact, zoneAfib, zoneOther } = generateZoneChart({
      artifactEventAreaPointsData,
      afibEventAreaPointsData,
      otherEventAreaPointsData,
      timezoneOffset,
    });

    // save data into global object
    dataDrawChart.current = {
      missingPointsData,
      artifactEventAreaPointsData,
      afibEventAreaPointsData,
      otherEventAreaPointsData,
      hrsData,
      zoneArtifact,
      zoneAfib,
      zoneOther,
      missingPointsZone,
      negativeHrsZones,
      unsaveArtifactEventsZones,
      unsaveAfibEventsZones,
      unsaveOtherEventsZones,
      artifactEventGuidesData,
      afibEventGuidesData,
      otherEventGuidesData,
      minHr,
      maxHr,
      isShowArtifactEvent,
      isShowAfibEvent,
      isShowOtherEvent,
      firstEpoch,
      isZoomAfterDrawChart,
      activeButton: getActiveButton(),
    };

    drawChartHr();
  };

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext) => {
      handleSelectColumn({
        selectedHr: findEleHrFromIndex(
          isNext ? currentSelectedIndexRef.current + 1 : currentSelectedIndexRef.current - 1,
        ),
        eventStart: updatedSelectedStrip?.start,
      });
    },

    resetCurrentSelect: () => {
      currentSelectedIndexRef.current = null;
    },
  }));

  useLayoutEffect(() => {
    if (!_.isEmpty(dataChart)) {
      isRenderingChart.current = true;
      maxHrRef.current = dataChart.maxHr;
      eventIndicatorsRef.current = dataChart.eventIndicators;
      createHrChart(dataChart);
    }
  }, [dataChart]);

  useEffect(
    () => () => {
      if (chartRef.current) {
        chartRef.current.destroy?.();
        chartRef.current = null;
      }
    },
    [],
  );

  useUpdateEffect(() => {
    if (chartRef.current) {
      const { series } = chartRef.current;
      let selectedHr;
      if (jumpDate) {
        const clickedCategory = moment(jumpDate).startOf('minutes').valueOf();
        selectedHr = findEleHrFromCategory(clickedCategory);
      } else if (currentSelectedIndexRef.current) {
        selectedHr = findEleHrFromIndex(currentSelectedIndexRef.current);
      } else if (!_.isEmpty(updatedSelectedStrip)) {
        const clickedCategory = moment(updatedSelectedStrip.start).startOf('minutes').valueOf();
        selectedHr = findEleHrFromCategory(clickedCategory);
      }
      if (!selectedHr) {
        //* Select first valid hr
        selectedHr = _.find(series[2].data, (item) => !_.isNil(item.y));
      }
      const type = getActiveButton();
      const eventListShow =
        type === TYPE_RHYTHM_EVENT_ENUM.AFIB
          ? eventListShowForAfibTab
          : [type, TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT];
      const isEventDeleted = updatedSelectedStrip?.type === TYPE_BEAT_ENUM.DELETED;
      const isEventChangeToHiddenType =
        updatedSelectedStrip?.type !== updatedSelectedStrip?.oldType &&
        !eventListShow.includes(updatedSelectedStrip?.type);
      isRenderingChart.current = false;
      handleSelectColumn({
        selectedHr,
        eventStart: updatedSelectedStrip?.start,
        isEventDeleted: isEventDeleted || isEventChangeToHiddenType,
      });
    }
  }, [loadChartCount]);

  // *: Re-select event (same date)
  useUpdateEffect(() => {
    if (!_.isEmpty(chartRef.current) && !_.isEmpty(updatedSelectedStrip) && !isLoadingHrChart) {
      //* Change updatedSelectedStrip same date
      if (
        currentSelectedStripId.current &&
        currentSelectedStripId.current === updatedSelectedStrip?.id
      ) {
        currentSelectedStripId.current = null;
        return;
      }
      if (preventReSelectByJumpDate.current) {
        preventReSelectByJumpDate.current = false;
        return;
      }
      if (preventReselectColumn.current) {
        preventReselectColumn.current = false;
      } else {
        let clickedCategory = moment(updatedSelectedStrip.start).startOf('minutes').valueOf();
        const updatedSelectedStripStart = moment(updatedSelectedStrip.start)
          .startOf('minutes')
          .valueOf();
        const updatedSelectedStripStop = moment(updatedSelectedStrip.stop)
          .startOf('minutes')
          .valueOf();
        if (
          updatedSelectedStripStart <= dataDrawChart.current.firstEpoch &&
          updatedSelectedStripStop >= dataDrawChart.current.firstEpoch
        ) {
          //* Event is between 2 days
          clickedCategory = dataDrawChart.current.firstEpoch;
        }
        //* V1
        // const type = getActiveButton();
        // const eventListShow = type === TYPE_RHYTHM_EVENT_ENUM.AFIB
        //   ? eventListShowForAfibTab
        //   : [type, TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT];
        //* V2
        const eventListShow = [TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT];
        const isEventDeleted = updatedSelectedStrip.type === TYPE_BEAT_ENUM.DELETED;
        const isEventChangeToHiddenType =
          updatedSelectedStrip.type !== updatedSelectedStrip.oldType &&
          !eventListShow.includes(updatedSelectedStrip.type);
        handleSelectColumn({
          selectedHr: findEleHrFromCategory(clickedCategory),
          eventStart: updatedSelectedStrip.start,
          isEventDeleted: isEventDeleted || isEventChangeToHiddenType,
        });
      }
    }
    if (!updatedSelectedStrip?.id && preventReSelectByJumpDate.current) {
      preventReSelectByJumpDate.current = false;
    }
  }, [updatedSelectedStrip?.id]);

  useUpdateEffect(() => {
    if (!_.isEmpty(chartRef.current) && jumpDate && !isRenderingChart.current) {
      const clickedCategory = moment(jumpDate).startOf('minutes').valueOf();
      handleSelectColumn({
        selectedHr: findEleHrFromCategory(clickedCategory),
        eventStart: updatedSelectedStrip?.start,
        isUpdateByJump: true,
      });
    }
  }, [jumpDate]);

  useUpdateEffect(() => {
    if (!_.isNil(currentSelectedIndexRef.current) && !_.isEmpty(chartRef.current)) {
      // selectedColumnInfoState (Recoil, Task 7) is typed (IEvent | IPoint)[], missing idEvent/oldType/etc. several call sites below read - atom-type gap.
      const selectedEvent: any = _.isEmpty(selectedColumnInfo)
        ? null
        : selectedColumnInfo[selectedEventIndex];
      if (selectedEvent?.type) {
        const eventType = selectedEvent.type || selectedEvent.oldType;
        const isArtifactEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
        const isAfibEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB;
        if (isOldUI) {
          chartRef.current.series[1].data[currentSelectedIndexRef.current].update(
            {
              className: isArtifactEvent
                ? '--artifact-event-clicked'
                : isAfibEvent
                  ? '--afib-event-clicked'
                  : '--event-clicked',
              color: isArtifactEvent
                ? CHART_COLOR.STEEL_GRAY_LIGHT
                : isAfibEvent
                  ? CHART_COLOR.AMBER_YELLOW_LIGHT
                  : CHART_COLOR.CRIMSON_RED_LIGHT,
            },
            false,
          );
        } else {
          chartRef.current.series[1].data[currentSelectedIndexRef.current].update(
            {
              className: isArtifactEvent
                ? '--artifact-event-clicked'
                : isAfibEvent
                  ? '--afib-event-clicked'
                  : '--event-clicked',
              color: isArtifactEvent
                ? CHART_COLOR.STEEL_GRAY_LIGHT
                : isAfibEvent
                  ? CHART_COLOR.AMBER_YELLOW_LIGHT
                  : CHART_COLOR.CRIMSON_RED_LIGHT,
            },
            false,
          );
          chartRef.current.series[2].data[currentSelectedIndexRef.current].update(
            { selected: true },
            false,
          );
        }
        // highlightSelectedEventArea({ foundEvent: selectedEvent, isAfibEvent, isArtifactEvent });
        // *: Select event card
        // - Click to event on HR chart
        // - Change event selected event info
        const activeButton = getActiveButton();
        if (
          selectedEvent.type === activeButton &&
          !isSelectEventDeleted.current &&
          !preventSetSelectedStrip.current
        ) {
          if (activeButton === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
            const selectedDateValue = getSelectedDateValue();
            const selectedDate = convertToMomentJs(selectedDateValue, timezoneOffset)?.format(
              'YYYY-MM-DD',
            );
            const selectedEventDate = convertToMomentJs(
              selectedEvent.start,
              timezoneOffset,
            )?.format('YYYY-MM-DD');
            if (selectedDate !== selectedEventDate) {
              setSelectedStrip(null);
            } else {
              // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent on some members - atom-type gap.
              setSelectedStrip((prevStrip: any) => {
                if (prevStrip?.idEvent !== selectedEvent?.idEvent && selectedEvent?.idEvent) {
                  preventReselectColumn.current = true;
                  currentSelectedStripId.current = selectedEvent.idEvent;
                  return selectedEvent;
                }
                currentSelectedStripId.current = prevStrip?.idEvent || null;
                return prevStrip;
              });
            }
          } else {
            // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent on some members - atom-type gap.
            setSelectedStrip((prevStrip: any) => {
              if (prevStrip?.idEvent !== selectedEvent?.idEvent && selectedEvent?.idEvent) {
                preventReselectColumn.current = true;
                return selectedEvent;
              }
              currentSelectedStripId.current = prevStrip?.idEvent || null;
              return prevStrip;
            });
          }
        }
      } else {
        if (isOldUI) {
          chartRef.current.series[1].data[currentSelectedIndexRef.current].update(
            {
              className: '--chart-clicked',
              color: CHART_COLOR.TURQUOISE_BLUE,
              y: maxHrRef.current,
            },
            false,
          );
        } else {
          chartRef.current.series[2].data[currentSelectedIndexRef.current].update(
            { selected: true },
            false,
          );
        }
        if (
          (getActiveButton() === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
            getActiveButton() === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) &&
          !(controlModal.isShowAddNewEvent || controlModal.isShowChangeDurationButtonType) &&
          hasClickedEvent.current
        ) {
          hasClickedEvent.current = false;
          setSelectedStrip(null);
        }
        // resetToOriginalZones();
      }
      // chartRef.current.redraw();
      debounceRedraw(chartRef.current);
    }
  }, [selectedEventIndex, selectedColumnInfo]);

  useUpdateEffect(() => {
    if (chartRef.current) {
      _.assign(dataDrawChart.current, {
        isShowArtifactEvent,
        isShowAfibEvent,
        isShowOtherEvent,
      });
      updateChartHr();
    }
  }, [isShowArtifactEvent, isShowAfibEvent, isShowOtherEvent]);

  useUpdateEffect(() => {
    if (controlModal.isShowAddNewEvent || controlModal.isShowChangeDurationButtonType) {
      preventSetSelectedStrip.current = true;
    } else {
      preventSetSelectedStrip.current = false;
    }
  }, [controlModal.isShowAddNewEvent, controlModal.isShowChangeDurationButtonType]);

  return {
    isLoadingHrChart,
    isLoadingHrChartOptions,
    isRenderingChart,
    isOldUI,
    idHtmlChart,
    zoomScale,
    zoomIndex,
    setExtremesRef,
    handleResetZoom,
    handleZoomOut,
    handleZoomIn,
  };
};

export default useChartHR;
